import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { User, Session } from "@supabase/supabase-js";

type UserProfile = {
  id: string;
  email: string;
  name: string;
  role: string;
  avatar_url?: string;
};

interface AuthContextType {
  user: UserProfile | null;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  signup: (email: string, name: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  forgotPassword: (email: string) => Promise<void>;
  resetPassword: (password: string, token: string) => Promise<void>;
  verifyOTP: (email: string, otp: string) => Promise<void>;
}

// Key for localStorage user profile
const USER_STORAGE_KEY = "mfo_user_profile";
// Supabase token key (this is the key we observed in localStorage)
const SUPABASE_TOKEN_KEY = "sb-gxjhhfllwtodskynhorr-auth-token";

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  // Initialize user state from localStorage if available
  const getInitialUser = (): UserProfile | null => {
    try {
      // Check if we have saved user profile
      const savedProfile = localStorage.getItem(USER_STORAGE_KEY);
      if (savedProfile) {
        return JSON.parse(savedProfile);
      }
      
      // If not, check if there's a Supabase token
      const supabaseToken = localStorage.getItem(SUPABASE_TOKEN_KEY);
      if (supabaseToken) {
        const tokenData = JSON.parse(supabaseToken);
        if (tokenData?.user) {
          // Create a minimal profile from the token data
          return {
            id: tokenData.user.id,
            email: tokenData.user.email,
            name: tokenData.user.user_metadata?.name || tokenData.user.email,
            role: tokenData.user.role || "authenticated",
            avatar_url: tokenData.user.user_metadata?.avatar_url || null
          };
        }
      }
      return null;
    } catch (error) {
      console.error("Error reading user from localStorage:", error);
      return null;
    }
  };

  const initialUser = getInitialUser();
  const [user, setUser] = useState<UserProfile | null>(initialUser);
  const [isLoading, setIsLoading] = useState(!initialUser); // Not loading if we have initial user
  const [session, setSession] = useState<Session | null>(null);
  const navigate = useNavigate();

  // Helper to update user in state and localStorage
  const updateUser = (profile: UserProfile | null) => {
    setUser(profile);
    if (profile) {
      localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(profile));
      
    } else {
      localStorage.removeItem(USER_STORAGE_KEY);

    }
  };

  const fetchUserProfile = async (user: User) => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();
      
      if (error) {
        console.error('Error fetching user profile:', error);
        // If we can't fetch the profile, create a minimal one from user data
        return {
          id: user.id,
          email: user.email || "",
          name: user.user_metadata?.name || user.email || "",
          role: "authenticated",
          avatar_url: user.user_metadata?.avatar_url || null
        } as UserProfile;
      }
      
      return data as UserProfile;
    } catch (error) {
      console.error('Unexpected error fetching profile:', error);
      return null;
    }
  };

  useEffect(() => {
    const checkSession = async () => {
      try {
        // If we already have a user from localStorage token, don't show loading state
        if (initialUser) {
         
          setIsLoading(false);
          
          // Verify the session in the background
          const { data } = await supabase.auth.getSession();
          const currentSession = data?.session;
          setSession(currentSession);
          
          if (currentSession?.user) {

            const profile = await fetchUserProfile(currentSession.user);
            if (profile && profile.id !== initialUser.id) {
      
              updateUser(profile);
            }
          } else {
 
            updateUser(null);
          }
        } else {
          setIsLoading(true);
          
          const { data } = await supabase.auth.getSession();
          const currentSession = data?.session;
          setSession(currentSession);
          
          if (currentSession?.user) {
            const profile = await fetchUserProfile(currentSession.user);
            if (profile) {
              updateUser(profile);
            }
          } else {
   
            updateUser(null);
          }
          setIsLoading(false);
        }
      } catch (error) {
        console.error("Session check error:", error);
        setIsLoading(false);
      }
    };

    checkSession();

    // Auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, newSession) => {
        setSession(newSession);
        
        if (event === 'SIGNED_IN' && newSession?.user) {
          // Use setTimeout to avoid potential deadlock in Supabase auth
          setTimeout(async () => {
            const profile = await fetchUserProfile(newSession.user);
            if (profile) {
              updateUser(profile);
            }
          }, 0);
        } else if (event === 'SIGNED_OUT') {
          updateUser(null);
        }
      }
    );

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const login = async (email: string, password: string) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      
      if (error) throw error;
      
      if (data.user) {
        const profile = await fetchUserProfile(data.user);
        if (profile) {
          updateUser(profile);
          return; 
        }
      }
      
      throw new Error("Failed to retrieve user profile");
    } catch (error: any) {
      toast({
        title: "Login failed",
        description: error.message || "Invalid credentials",
        variant: "destructive",
      });
      throw error;
    }
  };

  const signup = async (email: string, name: string, password: string) => {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            name,
          },
        },
      });
      
      if (error) throw error;
      
      toast({
        title: "Account created",
        description: "Please check your email to verify your account",
      });
      
      navigate("/login");
    } catch (error: any) {
      toast({
        title: "Sign up failed",
        description: error.message || "Failed to create account",
        variant: "destructive",
      });
      throw error;
    }
  };

  const logout = async () => {
    try {
      await supabase.auth.signOut();
      updateUser(null);
      
      // Clear all app data from localStorage
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith('mfo_')) {
          localStorage.removeItem(key);
        }
      });
      
      toast({
        title: "Logged out",
        description: "You have been logged out successfully",
      });
      navigate("/");
    } catch (error: any) {
      toast({
        title: "Logout failed",
        description: error.message || "An error occurred",
        variant: "destructive",
      });
      throw error;
    }
  };

  const forgotPassword = async (email: string) => {
    try {
      // Get the current origin for redirection after password reset
      const origin = window.location.origin;
      const redirectUrl = `${origin}/reset-password`;
      
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: redirectUrl,
      });
      
      if (error) throw error;
      
      toast({
        title: "Password reset email sent",
        description: "Check your email for the password reset link",
      });
      navigate("/login");
    } catch (error: any) {
      toast({
        title: "Failed to send reset email",
        description: error.message || "An error occurred",
        variant: "destructive",
      });
      throw error;
    }
  };

  const resetPassword = async (password: string, token: string) => {
    try {
      // Update the user's password
      const { error } = await supabase.auth.updateUser({
        password,
      });
      
      if (error) throw error;
      
      toast({
        title: "Password reset successful",
        description: "Your password has been updated",
      });
      navigate("/login");
    } catch (error: any) {
      toast({
        title: "Password reset failed",
        description: error.message || "An error occurred",
        variant: "destructive",
      });
      throw error;
    }
  };

  const verifyOTP = async (email: string, otp: string) => {
    try {
      // This is a placeholder since we're using email links instead of OTP
      // But we'll keep the function for future OTP implementation
      
      toast({
        title: "Verification method changed",
        description: "Please check your email for the verification link",
      });
      
      navigate("/login");
    } catch (error: any) {
      toast({
        title: "OTP verification failed",
        description: error.message || "Invalid OTP",
        variant: "destructive",
      });
      throw error;
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        login,
        signup,
        logout,
        forgotPassword,
        resetPassword,
        verifyOTP,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
